import classNames from "classnames";
import React from "react";
import linkArrow from "../assets/images/arrow-right-big.svg";
import { SanityNode } from "../config/build/models/node";
import { LinkInfo } from "../models/shared/LinkInfo";
import { isNullOrWhiteSpace, replaceWithUnicode } from "../shared/string";
import { iconsList } from "../utilities/iconsList";
import { isValidLink } from "../utilities/links";
import Button, { ButtonSize } from "./Buttons/Button";
import { PortableTextColorThemeProps } from "./PortableText/PortableText";
import SanityLink from "./SanityLink";

export interface IconCardBlockProps {
  iconCards?: {
    _key: string;
    title: string;
    link?: SanityNode;
    icon: string;
  }[];
  portableTextColorThemeProps?: PortableTextColorThemeProps;
  subtitle?: string;
  title?: string;
  darkTheme?: boolean;
  link?: LinkInfo;
}

const IconCardBlock = (props: IconCardBlockProps) => {
  const { darkTheme, title, subtitle, iconCards, link, portableTextColorThemeProps } =
    props;

  const {
    elementClasses: { h3Subheader: subtitleClasses = "text-subheader-primary" } = {}
  } = portableTextColorThemeProps || {};

  const getCardContents = (iconCard: {
    icon: string;
    title: string;
    link?: SanityNode;
  }) => (
    <div>
      <span
        className={classNames(
          "p-7 inline-block rounded-full ",
          darkTheme
            ? "bg-brand-primary-70 text-white"
            : "bg-brand-primary-95 text-brand-primary-20"
        )}
      >
        <svg width={44} height={44} className="fill-current order-1">
          <use xlinkHref={iconsList[iconCard.icon]} />
        </svg>
      </span>

      {!isNullOrWhiteSpace(iconCard.title) && (
        <h3 className="font-bold text-xl mt-7 uppercase text-brand-primary-20 h-12">
          {replaceWithUnicode(iconCard.title)}
        </h3>
      )}

      {!isNullOrWhiteSpace(iconCard.link?.title) && (
        <div
          className={classNames(
            "flex justify-center items-center",
            darkTheme ? "" : "text-brand-primary-95"
          )}
        >
          <svg className="w-8 h-8 fill-current card-arrow group-hover:text-brand-primary-70">
            <use xlinkHref={linkArrow.url} />
          </svg>
        </div>
      )}
    </div>
  );

  return (
    <div
      className={
        darkTheme
          ? "py-[3.75rem] md:py-[5rem] xl:py-32 overflow-auto bg-brand-primary-20 mb-[3rem] md:mb-[8rem]"
          : ""
      }
    >
      <aside className={darkTheme ? "mb-0" : ""}>
        <div>
          {!isNullOrWhiteSpace(title) && (
            <h2 className={darkTheme ? "text-white" : "text-current"}>{title}</h2>
          )}
          {!isNullOrWhiteSpace(subtitle) && (
            <h3
              className={`${subtitleClasses} mt-3 md:mt-3 lg:mt-3 xl text-base lg:text-xl uppercase font-bold ${
                darkTheme ? "text-neutral-75" : ""
              }`}
            >
              {subtitle}
            </h3>
          )}
        </div>
        <div className="flex justify-center mt-8">
          <ul className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-12 py-8 md:w-full">
            {iconCards?.map(iconCard => (
              <li key={iconCard._key}>
                {!isNullOrWhiteSpace(iconCard.link?.title) ? (
                  <SanityLink
                    reference={iconCard.link}
                    className={classNames(
                      "flex flex-col card-basic",
                      "py-12 px-6 mr-10 last:mr-0 text-center items-center h-full outline-none",
                      "group hover:border-brand-primary-70 hover:shadow-card-lifted hover:-translate-y-3 focus:outline-none focus:shadow-card-outline cursor-pointer",
                      darkTheme ? "bg-white" : ""
                    )}
                  >
                    {getCardContents(iconCard)}
                  </SanityLink>
                ) : (
                  <div
                    className={classNames(
                      "flex flex-col card-basic",
                      "py-12 px-6 mr-10 last:mr-0 text-center items-center h-full outline-none",
                      darkTheme ? "bg-white" : ""
                    )}
                  >
                    {getCardContents(iconCard)}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
        {isValidLink(link) && (
          <div className="flex justify-center">
            <Button
              className="xl:mr-6 mt-[1.87rem]"
              size={ButtonSize.Normal}
              color={darkTheme ? "primary-transparent" : "primary-inverted"}
              type="link"
              {...link}
            >
              {link.title}
            </Button>
          </div>
        )}
      </aside>
    </div>
  );
};

export default IconCardBlock;
